import {
  Injectable,
  inject,
  signal,
  computed,
  Signal,
  WritableSignal,
} from '@angular/core';
import { SchoolApiService } from './school-api.service';
import type { MySchoolPermissionsRes } from '../types/rest-api';

export function schoolSectionRoute(
  schoolId: string,
  section: 'jobs' | 'profile' | 'structure'
): unknown[] {
  switch (section) {
    case 'jobs':
      return ['/my-schools', schoolId, 'jobs'];
    case 'structure':
      return ['/my-schools', schoolId, 'structure'];
    case 'profile':
      return ['/my-schools', schoolId, 'schools', schoolId];
  }
}

/**
 * Per-school capability signals, fed by GET /v1/permissions/schools/{id}/my-role.
 * The backend already returns capability booleans (accounting for inheritance from
 * a parent authority), so this service does no role→capability mapping — it mirrors
 * AuthorityPermissionService's lazy-load + fetch-once + retry-on-error structure.
 */
@Injectable({ providedIn: 'root' })
export class SchoolPermissionService {
  private readonly api = inject(SchoolApiService);

  private readonly cache = new Map<
    string,
    WritableSignal<MySchoolPermissionsRes | null>
  >();
  /**
   * Whether the `my-role` request for a school has come back — successfully *or* not.
   * `cache` alone cannot express this: `null` there means "loading", "denied" and
   * "request failed" alike, so a caller that waits for capabilities before rendering
   * (see `SchoolProfileComponent`'s shimmer gate) would hang forever on a 403.
   */
  private readonly settledCache = new Map<string, WritableSignal<boolean>>();
  /** Schools whose fetch has been initiated (see AuthorityPermissionService note). */
  private readonly loadInitiated = new Set<string>();

  private getOrCreate(
    schoolId: string
  ): WritableSignal<MySchoolPermissionsRes | null> {
    if (!this.cache.has(schoolId)) {
      this.cache.set(schoolId, signal(null));
    }
    return this.cache.get(schoolId)!;
  }

  private getOrCreateSettled(schoolId: string): WritableSignal<boolean> {
    if (!this.settledCache.has(schoolId)) {
      this.settledCache.set(schoolId, signal(false));
    }
    return this.settledCache.get(schoolId)!;
  }

  /** Fetch the caller's capabilities for `schoolId` if not already fetched. */
  loadCapabilities(schoolId: string): void {
    if (this.loadInitiated.has(schoolId)) return;
    this.loadInitiated.add(schoolId);
    const s = this.getOrCreate(schoolId);
    const settled = this.getOrCreateSettled(schoolId);
    this.api.getMySchoolPermissions(schoolId).subscribe({
      next: res => {
        s.set(res);
        settled.set(true);
      },
      error: () => {
        s.set(null);
        settled.set(true);
        this.loadInitiated.delete(schoolId); // allow retry
      },
    });
  }

  /** Raw capabilities signal (`null` = loading or not yet fetched). */
  getCapabilities(schoolId: string): Signal<MySchoolPermissionsRes | null> {
    return this.getOrCreate(schoolId).asReadonly();
  }

  /** `true` once the capability request for `schoolId` resolved or failed. */
  settled(schoolId: string): Signal<boolean> {
    return this.getOrCreateSettled(schoolId).asReadonly();
  }

  private flag(
    schoolId: string,
    pick: (c: MySchoolPermissionsRes) => boolean
  ): Signal<boolean> {
    const caps = this.getOrCreate(schoolId);
    return computed(() => {
      const c = caps();
      return c !== null && pick(c);
    });
  }

  canViewProfile(schoolId: string): Signal<boolean> {
    return this.flag(schoolId, c => c.can_view_profile);
  }

  canEditProfile(schoolId: string): Signal<boolean> {
    return this.flag(schoolId, c => c.can_edit_profile);
  }

  canManageStructure(schoolId: string): Signal<boolean> {
    return this.flag(schoolId, c => c.can_manage_structure);
  }

  canViewJobs(schoolId: string): Signal<boolean> {
    return this.flag(schoolId, c => c.can_view_jobs);
  }

  canViewStructure(schoolId: string): Signal<boolean> {
    return this.flag(schoolId, c => c.can_view_structure);
  }

  canManageJobs(schoolId: string): Signal<boolean> {
    return this.flag(schoolId, c => c.can_manage_jobs);
  }

  canManageTeam(schoolId: string): Signal<boolean> {
    return this.flag(schoolId, c => c.can_manage_team);
  }

  firstViewableSection(
    schoolId: string
  ): 'jobs' | 'profile' | 'structure' | null {
    if (this.canViewJobs(schoolId)()) return 'jobs';
    if (this.canViewProfile(schoolId)()) return 'profile';
    if (this.canViewStructure(schoolId)()) return 'structure';
    return null;
  }

  /** Clear cached capabilities for one school (or all if omitted). */
  clearCache(schoolId?: string): void {
    if (schoolId !== undefined) {
      this.cache.delete(schoolId);
      this.settledCache.delete(schoolId);
      this.loadInitiated.delete(schoolId);
    } else {
      this.cache.clear();
      this.settledCache.clear();
      this.loadInitiated.clear();
    }
  }
}
